import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { UserProfile } from "@/types/user";
import { ChatPreview, ChatMessage } from "../types";
import { fetchRealChatPreviews } from "../services"; // Use the index export
import { useToast } from "@/hooks/use-toast";
import { Location } from "react-router-dom";
import { useDashboardData } from "@/components/dashboard/useDashboardData";

export function useChatInitialization(
  user: UserProfile | null,
  location: Location,
  handleSelectChat: (chatId: string) => void,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setChatPreviews: React.Dispatch<React.SetStateAction<ChatPreview[]>>,
  setRecipient: React.Dispatch<React.SetStateAction<UserProfile | null>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) {
  const { toast } = useToast();
  const { teamMembers } = useDashboardData();

  useEffect(() => {
    console.log("useChatInitialization - user:", user?.id);
    
    if (user) {
      console.log("Setting loading to true");
      setLoading(true);

      const loadChatPreviews = async () => {
        try {
          console.log("Loading chat previews");
          const previews = await fetchRealChatPreviews(user.id);

          console.log("Chat previews loaded:", previews.length);
          setChatPreviews(previews);

          // Check if there's a connect user in the location state
          const connectUserId = location.state?.connectUserId;
          const initialMessage = location.state?.initialMessage;

          if (connectUserId) {
            // Handle connecting with a specific user
            handleConnectWithUser(
              connectUserId,
              initialMessage,
              previews,
              user,
              teamMembers,
              handleSelectChat,
              setChatPreviews,
              setRecipient,
              setMessages,
              toast
            );
          } else if (previews.length > 0) {
            // Select first chat by default
            console.log("Selecting first chat:", previews[0].id);
            handleSelectChat(previews[0].id);
          }
        } catch (error) {
          console.error("Error loading chat previews:", error);
          toast({
            title: "Error",
            description: "Failed to load chat previews",
            variant: "destructive",
          });
        } finally {
          console.log("Setting loading to false");
          setLoading(false);
        }
      };

      loadChatPreviews();
    } else {
      // If no user, stop loading
      console.log("No user, setting loading to false");
      setLoading(false);
    }
  }, [user, location.state]);
}

// Helper function to handle connecting with a specific user
function handleConnectWithUser(
  connectUserId: string,
  initialMessage: string | undefined,
  previews: ChatPreview[],
  user: UserProfile,
  teamMembers: UserProfile[],
  handleSelectChat: (chatId: string) => void,
  setChatPreviews: React.Dispatch<React.SetStateAction<ChatPreview[]>>,
  setRecipient: React.Dispatch<React.SetStateAction<UserProfile | null>>,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  toast: any
) {
  // Find the chat with this user or create a new one
  const existingChat = previews.find((chat) => chat.user.id === connectUserId);

  if (existingChat) {
    handleSelectChat(existingChat.id);

    // Send initial message if provided
    if (initialMessage && user) {
      setTimeout(() => {
        const newMessage: ChatMessage = {
          id: uuidv4(),
          content: initialMessage,
          timestamp: new Date(),
          senderId: user.id,
          senderName: user.name,
          senderAvatar:
            user.avatarUrl ||
            `https://api.dicebear.com/7.x/personas/svg?seed=${user.name}`,
        };

        setMessages((prev) => [...prev, newMessage]);

        // Update chat preview
        setChatPreviews((prevChats) => {
          const updatedChats = prevChats.map((chat) =>
            chat.id === existingChat.id
              ? {
                  ...chat,
                  lastMessage: initialMessage,
                  timestamp: new Date(),
                }
              : chat
          );

          // Move chat to top
          const updatedChat = updatedChats.find(
            (chat) => chat.id === existingChat.id
          );
          if (updatedChat) {
            const filteredChats = updatedChats.filter(
              (chat) => chat.id !== existingChat.id
            );
            return [updatedChat, ...filteredChats];
          }

          return updatedChats;
        });
      }, 500); // Small delay to ensure chat is loaded
    }
  } else {
    // Create a new chat with this user
    const connectUser = teamMembers.find(
      (member) => member.id === connectUserId
    );

    if (connectUser) {
      const newChatId = uuidv4();

      const newChat: ChatPreview = {
        id: newChatId,
        user: connectUser,
        lastMessage: initialMessage || "Start a conversation...",
        timestamp: new Date(),
        unread: false,
        type: "individual", // Adding the missing type property
      };

      setChatPreviews((prev) => [newChat, ...prev]);

      // Select the new chat
      handleSelectChat(newChatId);
      setRecipient(connectUser);

      // Add initial message if provided
      if (initialMessage && user) {
        setTimeout(() => {
          const newMessage: ChatMessage = {
            id: uuidv4(),
            content: initialMessage,
            timestamp: new Date(),
            senderId: user.id,
            senderName: user.name,
            senderAvatar:
              user.avatarUrl ||
              `https://api.dicebear.com/7.x/personas/svg?seed=${user.name}`,
          };

          setMessages([newMessage]);
        }, 500); // Small delay to ensure chat is loaded
      } else {
        setMessages([]);
      }

      // Show a toast notification
      toast({
        title: "New conversation started",
        description: `You can now chat with ${connectUser.name}`,
      });
    }
  }
}
